// CrossfadeImage.jsx
import React, { useEffect, useRef, useState } from 'react';

const FADE_MS = 900; // adjust for slower/faster crossfade
const EASING = 'cubic-bezier(.2,.6,.2,1)';

export default function CrossfadeImage({ src, alt = '', className = '' }) {
  const [currentSrc, setCurrentSrc] = useState(src);
  const [prevSrc, setPrevSrc] = useState(null);
  const cleanupRef = useRef();

  useEffect(() => {
    if (!src || src === currentSrc) return;

    let cancelled = false;
    const img = new Image();
    img.decoding = 'async';
    img.loading = 'eager';
    img.src = src;

    const onReady = () => {
      if (cancelled) return;
      setPrevSrc(currentSrc); // keep old image visible
      setCurrentSrc(src); // show new image above it
      clearTimeout(cleanupRef.current);
      cleanupRef.current = setTimeout(() => {
        if (!cancelled) setPrevSrc(null); // drop old image after fade completes
      }, FADE_MS + 100);
    };

    if (img.complete) onReady();
    else img.onload = onReady;

    return () => {
      cancelled = true;
      clearTimeout(cleanupRef.current);
    };
  }, [src, currentSrc]);

  return (
    <div
      className={`event-image-container ${className}`}
      style={{
        '--fade-ms': `${FADE_MS}ms`,
        '--fade-ease': EASING,
      }}
    >
      {prevSrc && (
        <img
          src={prevSrc}
          alt=""
          className="event-image event-image--prev"
          aria-hidden="true"
          decoding="async"
          loading="eager"
        />
      )}
      {currentSrc && (
        <img
          src={currentSrc}
          alt={alt}
          className="event-image event-image--current"
          decoding="async"
          loading="eager"
          fetchpriority="high"
        />
      )}
      {/* optional: tiny overlay avoids background peeking on slow GPUs */}
      <div className="image-guard" aria-hidden />
    </div>
  );
}
